import { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Formik } from "formik";
import { Slide, ToastContainer, toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { DefaultLayout } from "../../../../layouts/Default";
import { TopNavigation } from "../../../../themes/Default/components/TopNavigation";
import { Button } from "../../../../themes/Default/components/TopNavigation/components/Button";
import { ButtonBack } from "../../../../themes/Default/components/TopNavigation/components/Button";
import { Tabs } from "../../../../themes/Default/components/Tabs";
import { Main } from "../../../../themes/Default/components/Main";
import { Breadcrumps } from "../../../../themes/Default/components/Breadcrumps";
import { Link } from "../../../../themes/Default/components/Breadcrumps/components/Link";
import { Form } from "../../../../themes/Default/components/Form";
import { Row } from "../../../../themes/Default/components/Form/components/Row";
import { Field } from "../../../../themes/Default/components/Form/components/Row/components/Field";
import { Title } from "../../../../themes/Default/components/Title";
import { Wizard } from "../../../../themes/Default/components/Wizard";
import { Step } from "../../../../themes/Default/components/Wizard/Step";
import { groupsManagement } from "../../../../hooks/api/GroupsManagement";
import { Textarea } from "../../../../themes/Default/components/Form/components/Textarea";
import { Select } from "../../../../themes/Default/components/Form/components/Select";
import { reportTypesManagement } from "../../../../hooks/api/ReportTypesManagement/ReportTypesManagement";
import { prepareSelect } from "../../../../services/core/Select";
import { useUserToken } from "../../../../hooks/core/UserToken";
import { roles } from "../../../../services/utils/Roles";

const EditReport = () => {
    const navigate = useNavigate();
    const { t } = useTranslation("common");
    const { groupId } = useParams();
    const { reportId } = useParams();
    const formRef = useRef(null);
    const [formErrors, setFormErrors] = useState([]);

    const { useUpdateReportMutation, useViewReportMutation, useViewGroupMutation} = groupsManagement;
    const { useReportTypesListMutation } = reportTypesManagement;
    const [editReport] = useUpdateReportMutation();
    const [viewReport] = useViewReportMutation();
    const [reportTypeList] = useReportTypesListMutation();

    const [reportTypes, setReportTypes] = useState([]);
    const [hasTests, setHasTests] = useState(false);
    const [hasProgress, setHasProgress] = useState(false);
    const [hasPlan, setHasPlan] = useState(false);
    const [viewGroup] = useViewGroupMutation();
    const [report, setReport] = useState();

    const tabs = [
        { title: t('groups.attendance'),onClick: () => gotoAttendanceTab(groupId)  },
        ...(hasPlan ? [
            { title: t('groups.planning'), onClick: () => gotoPlanningTab(groupId)},
        ] : []),
        ...(hasProgress ? [
            { title: t('groups.progress'), onClick: () => gotoProgressTab(groupId)},
        ] : []),
        ...(hasTests ? [
            { title: t('groups.progressTest'), onClick: () => gotoProgressTestsTab(groupId) }
        ] : []),
        { title: t('reports.title'),isActive: true},
        { title: t('documents'), onClick: () => gotoDocumentsTab(groupId)},
        { title: t('notes'), onClick: () => gotoNotesTab(groupId)},
    ];

    const [{ getDecoded }] = useUserToken();
    const decodedToken = getDecoded();
    if (
        decodedToken?.roles.includes(roles.SUPER_ADMIN) ||
        decodedToken?.roles.includes(roles.SECRETARY) ||
        decodedToken?.roles.includes(roles.DIRECTOR) || 
        decodedToken?.roles.includes(roles.DIRECTOR_OF_STUDIES)
    ) {
        tabs.push({
            title: t("settings.title"),
            onClick: () => gotoEditTab(groupId),
        });
    }

    const gotoPlanningTab = (groupId) => {
        navigate("/groups/" + groupId + "/planning");
    }

    const gotoProgressTestsTab = (groupId) => {
        navigate("/groups/" + groupId + "/tests");
    }

    const gotoProgressTab = (groupId) => {
        navigate("/groups/" + groupId + "/progress");
    }

    const gotoNotesTab = (groupId) => {
        navigate("/groups/" + groupId + "/notes");
    }

    const gotoDocumentsTab = (groupId) => {
        navigate("/groups/" + groupId + "/documents");
    }

    const gotoAttendanceTab = (groupId) => {
        navigate("/groups/" + groupId + "/attendance");
    }

    const gotoEditTab = (groupId) => {
        navigate("/groups/edit/" + groupId);
    }

    const gotoReportsStudent = () => {
        navigate("/groups/" + groupId + "/student-reports/" + reportId);
    }
    
    const onSave = () => {
        if (formRef.current) {
            formRef.current.handleSubmit();
        }
    };

    const onSaved = (values) => {
        const body = {...values};
        editReport({body, groupId, reportId}).then((result) => {
            if (result?.error) {
                setFormErrors(result?.error?.data?.error?.violations);
                toast.error(result.error.data.error.responseMessage);
            } else {
                toast.info(result.data.info.responseMessage);
            }
        });
        
    };

    useEffect(() => {
        viewGroup(groupId).then((result) => {
            setHasTests(result?.data?.hasTests);
            setHasProgress(result?.data?.hasProgress);
            setHasPlan(result?.data?.hasPlan);
        });
        viewReport({groupId, reportId}).then((result) => {
            formRef.current.setValues(result?.data);
            setReport(result?.data);
        });  
        reportTypeList().then((result) => {
            setReportTypes(prepareSelect(result?.data?.records));
        }); 
    }, []);


    return (
        <DefaultLayout>
            
            <TopNavigation>
                <ButtonBack to={"/groups/" + groupId + "/reports"} />
                {report?.hasOneOrMoreReport ? null : <Button title={t('save')} icon="fa-regular fa-floppy-disk" onClick={onSave}></Button>}
            </TopNavigation>
            <Tabs tabs={tabs} />
            <Main>
                <Title>{t('reports.title')}</Title>
                <Breadcrumps>
                    <Link title={t("groups.title")} icon="fa-user-group" />
                    <Link title={t("reports.title")} isActive={true} />
                </Breadcrumps>
                <br/>
                <br/>
                <br/>
                <Wizard>
                    <Step title={t("reports.type")} isActive={true} />
                    <Step title={t("reports.individualReports")} isChecked={true} onClick={gotoReportsStudent} />
                </Wizard>
                <br/>
                <br/>
                <br/>
                <Formik
                    innerRef={formRef}
                    initialValues={{ 
                        title: "",
                        description: ""
                    }}
                    onSubmit={onSaved}
                >
                    {({ values, handleChange }) => (
                        <Form>
                            <Row>
                                <Field>
                                    <Select 
                                        name="reportType" 
                                        label={t('groups.type')} 
                                        onChange={handleChange} 
                                        required={true}
                                        options={reportTypes} 
                                        selectedValue={values.reportTypeId} 
                                        error={formErrors?.reportType}
                                        searchInDropdown={report?.hasOneOrMoreReport}
                                        disabled={report?.hasOneOrMoreReport}
                                    />
                                </Field>
                            </Row>
                            <Row>
                                <Field isFullWidth={true}>
                                    <Textarea 
                                        name="description" 
                                        label={t('test.note')} 
                                        rows={5}
                                        value={values.description} 
                                        onChange={handleChange} 
                                        error={formErrors?.description}
                                        disabled={report?.hasOneOrMoreReport}
                                    />
                                </Field>
                            </Row>
                        </Form>
                    )}
                </Formik>
            </Main>
        </DefaultLayout>
    );
};

export default EditReport;
